import { useEffect, useState } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const RELOAD_FLAG = 'app:lastChunkReloadAt';
const RELOAD_GUARD_MS = 30_000;

/** Detects the very common "Failed to fetch dynamically imported module" error
 *  that browsers throw after a redeploy when the cached index.html still
 *  references chunk filenames that no longer exist on the server.
 */
export function isChunkLoadError(error: unknown): boolean {
  const msg =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';
  if (!msg) return false;
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('ChunkLoadError') ||
    /Loading chunk \d+ failed/i.test(msg)
  );
}

/** Throttled hard-reload — runs once per RELOAD_GUARD_MS window so we don't
 *  hot-loop if a chunk is genuinely missing rather than stale. */
export function recoverFromChunkError(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage blocked — still attempt the reload */
  }
  window.location.reload();
  return true;
}

type Props = {
  /** Caught error (Error, string, RouteError shape, anything). */
  error: unknown;
  /** Called when the user clicks "Try again". Falls back to a hard reload. */
  onRetry?: () => void;
  /** If true, render only the inner card (no full-page chrome). */
  inline?: boolean;
};

/** Friendly, user-facing error screen. Used as the routes' `errorElement` and
 *  as the React error boundary fallback. Auto-recovers from stale-chunk
 *  errors and otherwise shows a tidy retry / go-home UI. */
export function ErrorScreen({ error, onRetry, inline }: Props) {
  const { t } = useTranslation('common');
  const [showDetails, setShowDetails] = useState(false);

  // Stale-chunk after redeploy: try a one-shot reload before showing the UI.
  useEffect(() => {
    if (isChunkLoadError(error)) recoverFromChunkError();
  }, [error]);

  const detail = readableError(error);
  const isChunk = isChunkLoadError(error);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  const card = (
    <Card className="mx-auto w-full max-w-lg">
      <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            {isChunk
              ? (t('error.staleChunkTitle') as string)
              : (t('error.title') as string)}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isChunk
              ? (t('error.staleChunkBody') as string)
              : (t('error.body') as string)}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={handleRetry}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('error.retry') as string}
          </Button>
          <Button variant="outline" asChild>
            <a href="/dashboard/projects">
              <Home className="mr-2 h-4 w-4" />
              {t('error.home') as string}
            </a>
          </Button>
        </div>

        {detail && (
          <div className="w-full">
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => setShowDetails((p) => !p)}
            >
              {showDetails
                ? (t('error.hideDetails') as string)
                : (t('error.showDetails') as string)}
            </button>
            {showDetails && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md border bg-muted/40 p-3 text-left text-[11px] leading-relaxed text-muted-foreground">
                {detail}
              </pre>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (inline) return card;

  return (
    <div
      className={cn(
        'flex min-h-[60vh] w-full items-center justify-center p-6',
      )}
    >
      {card}
    </div>
  );
}

function readableError(error: unknown): string {
  if (!error) return '';
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    const e = error as { status?: number; statusText?: string; data?: unknown; message?: string };
    const status = e.status ? `HTTP ${e.status}${e.statusText ? ` ${e.statusText}` : ''}` : '';
    const data =
      typeof e.data === 'string' ? e.data : e.data ? JSON.stringify(e.data, null, 2) : '';
    return [status, e.message, data].filter(Boolean).join('\n');
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}
